import { ColumnDef } from "@tanstack/react-table";
import {
  Ban,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  FileCheck2,
  Send,
  ShoppingCart,
  ThumbsUp,
  Wallet,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import {
  createSortableHeader,
  DataTable,
} from "../../../components/ui/data-table";
import { DatePicker } from "../../../components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  formatCurrency,
  formatDate,
} from "../../entrego/pages/saldo-consulta/utils/format";
import { useOverview } from "../hooks/use-offer-tracking";
import {
  LAST_STAGE_ORDER,
  LastStageFilter,
  OpsFilter,
  OverviewFunnel,
  OverviewRow,
} from "../types";
import { LastStageBadge } from "./last-stage-badge";

interface OverviewViewProps {
  dbName: string;
  ops: OpsFilter;
}

const PAGE_SIZE = 50;

function toIsoDate(d: Date): string {
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
}

const columns: ColumnDef<OverviewRow>[] = [
  {
    accessorKey: "ops",
    header: "OPS",
    cell: ({ row }) => (
      <Badge variant="outline" className="uppercase">
        {row.original.ops}
      </Badge>
    ),
  },
  {
    accessorKey: "cnpjCpf",
    header: createSortableHeader("CPF/CNPJ"),
    cell: ({ row }) => <span className="font-mono">{row.original.cnpjCpf}</span>,
  },
  {
    accessorKey: "telefone",
    header: "Telefone",
    cell: ({ row }) => <span className="font-mono">{row.original.telefone}</span>,
  },
  {
    accessorKey: "valorFace",
    header: createSortableHeader("Valor Face"),
    cell: ({ row }) => formatCurrency(row.original.valorFace),
  },
  {
    accessorKey: "valorCompra",
    header: createSortableHeader("Valor Compra"),
    cell: ({ row }) => formatCurrency(row.original.valorCompra),
  },
  {
    accessorKey: "dataVencimento",
    header: "Vencimento",
    cell: ({ row }) => formatDate(row.original.dataVencimento),
  },
  {
    id: "lastStage",
    // Ordena pela progressão do funil, não pelo alfabeto do rótulo
    accessorFn: (row) => LAST_STAGE_ORDER.indexOf(row.lastStage),
    header: createSortableHeader("Parou em"),
    cell: ({ row }) => <LastStageBadge stage={row.original.lastStage} bloqueio={row.original.bloqueio} />,
  },
];

const STAGE_FILTER_OPTIONS: { value: LastStageFilter; label: string }[] = [
  { value: "all", label: "Todos os estágios" },
  { value: "bloqueado", label: "Bloqueado" },
  { value: "nao_enviada", label: "Não enviada" },
  { value: "arquivos_prontos", label: "Arquivos prontos" },
  { value: "enviada_wts", label: "Enviada (WTS)" },
  { value: "aceite", label: "Aceite" },
  { value: "liquidado", label: "Liquidado" },
  { value: "comprado", label: "Comprado" },
  { value: "baixado", label: "Baixado" },
];

interface FunnelCard {
  key: keyof OverviewFunnel;
  title: string;
  icon: React.ReactNode;
}

const FUNNEL_CARDS: FunnelCard[] = [
  { key: "arquivosProntos", title: "Arquivos prontos", icon: <FileCheck2 className="h-5 w-5" /> },
  { key: "enviadas", title: "Enviadas (WTS)", icon: <Send className="h-5 w-5" /> },
  { key: "aceites", title: "Aceites", icon: <ThumbsUp className="h-5 w-5" /> },
  { key: "liquidadas", title: "Liquidadas", icon: <Wallet className="h-5 w-5" /> },
  { key: "compradas", title: "Compradas", icon: <ShoppingCart className="h-5 w-5" /> },
  { key: "baixadas", title: "Baixadas", icon: <CheckCircle2 className="h-5 w-5" /> },
];

export function OverviewView({ dbName, ops }: OverviewViewProps) {
  const [date, setDate] = useState<Date | undefined>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1); // data_romaneio costuma ser D-1
    return d;
  });
  const [page, setPage] = useState(1);
  const [lastStage, setLastStage] = useState<LastStageFilter>("all");

  const isoDate = date ? toIsoDate(date) : "";

  // volta para a primeira página ao trocar OPS, data ou estágio
  useEffect(() => {
    setPage(1);
  }, [ops, isoDate, lastStage]);

  const { data, isLoading, isError, isFetching, refetch } = useOverview({
    dbName,
    ops,
    date: isoDate,
    lastStage,
    page,
    pageSize: PAGE_SIZE,
    enabled: !!isoDate,
  });

  const pagination = data?.pagination;
  const totalPages = pagination?.totalPages ?? 1;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">
            Data do romaneio
          </label>
          <DatePicker date={date} setDate={setDate} />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">
            Parou em
          </label>
          <Select
            value={lastStage}
            onValueChange={(v) => setLastStage(v as LastStageFilter)}
          >
            <SelectTrigger className="w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STAGE_FILTER_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {lastStage !== "all" && (
          <Button
            variant="ghost"
            size="sm"
            className="mb-1"
            onClick={() => setLastStage("all")}
          >
            Limpar filtro
          </Button>
        )}

        {data && !data.wtsOk && (
          <Badge variant="destructive" className="mb-1">
            WTS indisponível — "enviadas" pode estar incompleto
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-8">
        {FUNNEL_CARDS.map((c) => (
          <Card key={c.key}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground">
                    {c.title}
                  </p>
                  <p className="mt-2 text-2xl font-bold">
                    {data ? data.funnel[c.key] : "—"}
                  </p>
                </div>
                <div className="text-muted-foreground">{c.icon}</div>
              </div>
            </CardContent>
          </Card>
        ))}
        <Card className="border-destructive/40">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-destructive">
                  Não enviadas
                </p>
                <p className="mt-2 text-2xl font-bold text-destructive">
                  {data ? data.funnel.naoEnviadas : "—"}
                </p>
              </div>
              <XCircle className="h-5 w-5 text-destructive" />
            </div>
          </CardContent>
        </Card>
        <Card
          className="cursor-pointer border-destructive/40"
          onClick={() => setLastStage("bloqueado")}
          title="Barrados por bloqueio (fora dos candidatos). Clique para filtrar."
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-destructive">
                  Bloqueados
                </p>
                <p className="mt-2 text-2xl font-bold text-destructive">
                  {data ? data.funnel.bloqueados ?? 0 : "—"}
                </p>
              </div>
              <Ban className="h-5 w-5 text-destructive" />
            </div>
          </CardContent>
        </Card>
      </div>

      {isError && (
        <p className="text-sm text-destructive">
          Erro ao carregar o rastreamento. Tente novamente.
        </p>
      )}

      <DataTable
        columns={columns}
        data={data?.rows ?? []}
        title={
          lastStage === "all"
            ? "Candidatos da data"
            : `Candidatos da data · ${STAGE_FILTER_OPTIONS.find((o) => o.value === lastStage)?.label}`
        }
        loading={isLoading}
        onRefresh={() => refetch()}
        searchPlaceholder="Buscar por CPF/CNPJ ou telefone..."
        exportFileName={`rastreamento-${ops}-${isoDate}`}
        pageSize={PAGE_SIZE}
      />

      {pagination && pagination.totalRows > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {pagination.totalRows} candidato(s)
            {lastStage !== "all" && " no estágio filtrado (os cards acima seguem contando a data inteira)"}
            {" "}· página {pagination.page} de {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages || isFetching}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Próxima
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
